'use client'

/**
 * Проверенный контент платформы VCENTRE.ONE.
 * Всё, что ещё не работает, помечено статусом "soon" / "dev" — не выдаём за готовое.
 */

export type Status = "live" | "dev" | "soon" | "idea";

export const STATUS_LABEL: Record<Status, string> = {
  live: "Работает",
  dev: "В разработке",
  soon: "Скоро",
  idea: "Направление развития",
};

export const HERO = {
  eyebrow: "VCENTRE.ONE · платформа для бизнеса",
  title: ["Все клиенты.", "Все каналы.", "Одна платформа."],
  subtitle:
    "Готовая система управления и стандартное приложение бизнеса уже с BASE. Расширяйте приложение, подключайте сайт, расширения и бизнес-AI по мере роста — клиенты, данные и процессы остаются в одном контуре.",
  note: "Всё связано",
};

export const OLD_WAY = [
  "Сайт — отдельно",
  "CRM — отдельно",
  "Заявки — в почте и мессенджерах",
  "Бонусы — в таблице",
  "Рассылки — в третьем сервисе",
  "Офлайн-клиенты — в блокноте",
];

export const NEW_WAY = [
  "Один профиль клиента",
  "Одна история отношений",
  "Заявки, сделки, задачи в одном контуре",
  "Своё приложение и свой сайт",
  "Коммуникации и лояльность внутри системы",
  "Офлайн-клиент — полноценный клиент",
];

export const LEVELS = [
  {
    index: "01",
    title: "Центр бизнеса",
    lead: "Самостоятельная рабочая система внутри платформы.",
    items: [
      "Клиенты, контакты и единый профиль",
      "CRM: заявки, сделки, задачи",
      "Сотрудники, роли и права доступа",
      "Каталог, заказы, оплаты, документы",
      "Чаты, коммуникации, история",
      "Лояльность, рефералы, квизы, сценарии",
      "Мероприятия и билеты",
      "Аналитика и автоматизация",
    ],
  },
  {
    index: "02",
    title: "Свои цифровые каналы",
    lead: "Стандартное приложение с BASE. В PRO — расширенное приложение или сайт, в PRO+ и GENIUS — оба канала одновременно.",
    items: [
      "Готовое приложение со стандартными страницами",
      "Глобальное брендирование и экран загрузки",
      "Расширенный конструктор выбранного канала",
      "Полноценный сайт со своей админкой",
      "Личный кабинет клиента",
      "Регистрация, заявки, покупки, записи",
      "Документы, история, бонусы",
      "Персональные предложения и уведомления",
    ],
  },
  {
    index: "03",
    title: "Внешние каналы",
    lead: "Платформа не требует отказываться от текущей инфраструктуры.",
    items: [
      "Внешние CRM и учётные системы",
      "1С",
      "Банки и платёжные системы",
      "Мессенджеры",
      "Внешние сервисы и каталоги",
      "Аналитика",
    ],
  },
];

export const FLOW_CHAIN = [
  "Клиент оставил заявку на сайте",
  "Появился контакт",
  "Заявка попала в CRM",
  "Назначился менеджер",
  "Появилась задача",
  "Клиент получил уведомление",
  "Менеджер продолжил работу",
];

export const CHANNELS = [
  {
    title: "Приложение компании",
    text: "Готовое стандартное приложение с BASE: главная, каталог, кабинет, лояльность, рефералы и документы. Расширенный конструктор и дополнительные сценарии — с PRO при выборе приложения, а также в PRO+ и GENIUS.",
    bullets: ["Стандартные страницы", "Каталог без лимита позиций", "Брендирование", "Лояльность и рефералы"],
    to: "/platform/app",
  },
  {
    title: "Сайт со своей админкой",
    text: "Полноценный сайт можно выбрать в PRO, сохранив стандартное приложение. PRO+ и GENIUS объединяют расширенное приложение и сайт с общими CRM, каталогом и клиентами.",
    bullets: ["Своя административная часть", "Каталог из платформы", "Формы и квизы", "Заявки сразу в CRM"],
    to: "/platform/websites",
  },
  {
    title: "Клиентские интерфейсы",
    text: "Личные кабинеты и другие интерфейсы под задачи бизнеса: для клиентов, партнёров или сотрудников.",
    bullets: ["Кабинет клиента", "Документы и история", "Персональные предложения", "Роли и доступы"],
    to: "/platform/modules",
  },
];

export const OUTCOMES = [
  {
    title: "Не терять клиента между каналами",
    text: "Заявка из приложения, с сайта, из переписки или от менеджера попадает в один контур.",
  },
  {
    title: "Единая история отношений",
    text: "Обращения, покупки, оплаты, документы и коммуникации собраны в профиле клиента.",
  },
  {
    title: "Онлайн и офлайн вместе",
    text: "Офлайн-клиента менеджер создаёт вручную, а позже связывает с его цифровым профилем.",
  },
  {
    title: "Управление продажами из центра",
    text: "Заявки, сделки, задачи и ответственные — без переключения между сервисами.",
  },
  {
    title: "Автоматизация рутины",
    text: "Сценарии, уведомления, напоминания и типовые действия выполняются без ручной работы.",
  },
  {
    title: "Повторные продажи",
    text: "Личный кабинет, лояльность, рассылки и персональные предложения работают на возврат клиента.",
  },
];

export const CAPABILITIES = [
  "CRM и клиенты",
  "Заявки и сделки",
  "Задачи и сотрудники",
  "Каталог и заказы",
  "Оплаты и документы",
  "Чаты и коммуникации",
  "Программа лояльности",
  "Рефералы",
  "Квизы и формы",
  "Сценарии и автоматизация",
  "Рассылки и push",
  "Мероприятия и билеты",
  "Аналитика",
  "AI-функции",
  "Роли и права",
  "Интеграции",
];

export const PROOF = [
  {
    title: "Спецноминация конкурса «Создай НАШЕ»",
    org: "Ростелеком",
    href: "https://www.company.rt.ru/press/news/d478663/",
  },
  {
    title: "Проект в информационном поле Агентства стратегических инициатив",
    org: "АСИ",
    href: "https://asi.ru/news/207276/",
  },
  {
    title: "Участие в форуме «Энергия возможностей»",
    org: "Форум",
    href: "https://forum-47.ru/",
  },
  {
    title: "Продукт экосистемы VCENTRE.IT",
    org: "VCENTRE.IT",
    href: "https://it.vcentre.pro/",
  },
];
